import {objectFromEntries} from '@benchling/js-utils';
import {Unit} from '@benchling/units';
import {uniq} from 'underscore';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {
  bulkFetchMeasurementUnits,
  densityUnitTypePredicate,
  massUnitTypePredicate,
  molarityUnitTypePredicate,
  volumeUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  ServerResolutionResult,
  UnitsCustomResolutionQuery,
  CustomResolutionQueryType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {EMPTY_TYPED_VALUE} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolvedValueInfo,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {UnitReferenceTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';

export const DEFAULT_MOL_UNIT_SYMBOL = 'mol';
export const DEFAULT_MASS_UNIT_SYMBOL = 'g';
export const DEFAULT_VOLUME_UNIT_SYMBOL = 'L';
export const DEFAULT_DENSITY_UNIT_SYMBOL = 'g/L';
export const DEFAULT_MOLARITY_UNIT_SYMBOL = 'M';

// Chemistry equations use g as the base unit for mass instead of kg, so we often need to convert from kg to g.
export const KG_TO_G_CONVERSION_FACTOR = 1000;

// For stoichiometry amount calculations, we can use either mass or volume units.
export function stoichiometryAmountUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return massUnitTypePredicate(unitType) || volumeUnitTypePredicate(unitType);
}

// For stoichiometry concentration calculations, we can use either density or molarity units.
export function stoichiometryConcentrationUnitTypePredicate(unitType: Unit['unitType']): boolean {
  return densityUnitTypePredicate(unitType) || molarityUnitTypePredicate(unitType);
}

/**
 * Get the factor (from GraphQL cache) to convert a unit reference to another unit.
 *
 * IMPORTANT: GraphQL stores conversion factors relative to SI base units (kg is BaseUnit for mass),
 * but chemistry calculations use g/mol for molecular weight. We need to convert from
 * the unit's SI base (kg) to chemistry standard (g).
 *
 * @param sourceUnitTypedValue - The unit typed value to convert
 * @param targetUnitSymbol - The symbol of the measurement unit to convert to
 * @returns The conversion factor as a number, null if unit cannot be determined, or if the unit
 * is not yet in the cache (triggers a background fetch for subsequent calls).
 */
export function getStandardChemistryUnitConversionFactor(
  unit: MeasurementUnit | undefined,
  additionalConversionFactor: number = 1
): number | undefined {
  if (!unit) {
    return undefined;
  }
  const conversionFactorToSIBase = parseFloat(unit.conversionFactor);
  return conversionFactorToSIBase * additionalConversionFactor;
}

/** Returns loaded units as an array of MeasurementUnits */
export async function bulkResolveUnitsToServerResolutionResult(
  unitsQueries: Array<UnitsCustomResolutionQuery>
): Promise<Array<ServerResolutionResult>> {
  const uniqueUnitIds = uniq(unitsQueries.flatMap((query) => query.unitIds));
  const units = await bulkFetchMeasurementUnits(uniqueUnitIds);
  const unitsById = objectFromEntries(units.map((unit) => [unit.id, unit]));
  return unitsQueries.map((query) =>
    makeCustomResolutionResultFromTypedValue({
      type: BaseTypedValueType.ARRAY,
      rawValue: query.unitIds.map((unitId) => ({
        type: BaseTypedValueType.JSON,
        jsonValue: unitsById[unitId],
      })),
    })
  );
}

/** Given a bunch of TypedValues, resolve any Units from them, loading them as Measurement units. */
export function resolveUnitsFromTypedValues(
  typedValues: Array<TypedValue | null>,
  resolveFromResolutionQuery: ResolveQueryFn
): {
  units?: Array<MeasurementUnit>;
  error?: ResolvedValueInfo['error'];
} {
  const unitIds: Array<string> = typedValues
    .filter(
      (typedValue): typedValue is UnitReferenceTypedValue =>
        typedValue != null && typedValue.type === BaseTypedValueType.UNIT_REFERENCE
    )
    .map((unitTypedValue) => unitTypedValue.unitSummary.id);

  const {resolvedValue, error} = resolveFromResolutionQuery(
    EMPTY_TYPED_VALUE,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.UNITS,
      unitIds,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // If we have an error from trying to fetch the units, return the error and don't try to solve. This is
  // usually just the loading "error" while we are still fetching the units.
  if (error != null) {
    return {
      error,
    };
  }
  assert(resolvedValue.type === BaseTypedValueType.ARRAY, 'Resolved value must be an array', {
    resolvedValue,
  });

  return {
    units: resolvedValue.rawValue.map((unit) => {
      assert(unit.type === BaseTypedValueType.JSON, 'Unit must be a JSON value', {unit});
      return unit.jsonValue;
    }),
  };
}
